/**
 * LocationWithPortraits Component
 *
 * Composite widget that displays a location image with character portraits
 * overlapping its bottom border (50% over the image). Used for section69
 * and similar adventures to visually group location and present characters.
 */

import React from 'react';
import { generateImageUrl } from '../../utils/imageUtils';
import PortraitImage from './PortraitImage';

export interface LocationWithPortraitsProps {
  /** Location image ID (e.g. section69:lounge) */
  imageId: string;
  /** Portrait IDs for characters at the location */
  portraitIds?: string[];
  /** Optional alt text for the location image */
  alt?: string;
  /** Additional CSS classes */
  className?: string;
}

const PORTRAIT_SIZE = 64; // thumbnail size - 50% overlap = 32px overlap
const PORTRAIT_VARIANT = 'thumbnail' as const;

/**
 * Location image with portraits overlapping the bottom border (50% over).
 */
export const LocationWithPortraits: React.FC<LocationWithPortraitsProps> = ({
  imageId,
  portraitIds = [],
  alt,
  className = '',
}) => {
  const locationImageUrl = generateImageUrl(
    imageId,
    'location-image',
    'card'
  );
  const altText = alt ?? imageId.replace(/_/g, ' ').replace(/:/g, ' - ');

  return (
    <div
      className={`location-with-portraits rich-text-location-with-portraits ${className}`.trim()}
      style={{
        position: 'relative',
        overflow: 'visible',
        paddingBottom: portraitIds.length > 0 ? PORTRAIT_SIZE / 2 : 0,
      }}
    >
      {/* Location image */}
      <div
        style={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '8px',
        }}
      >
        <img
          src={locationImageUrl}
          alt={altText}
          className="rich-text-image rich-text-location-image"
          style={{
            width: '100%',
            display: 'block',
            objectFit: 'cover',
            aspectRatio: '16/9',
          }}
        />
      </div>

      {/* Portraits overlapping the bottom - 50% over the image border */}
      {portraitIds.length > 0 && (
        <div
          className="location-with-portraits__portraits"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
            transform: 'translateY(-30%)',
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          {portraitIds.map((portraitId, index) => (
            <div
              key={`${portraitId}-${index}`}
              style={{
                flexShrink: 0,
                width: PORTRAIT_SIZE,
                height: PORTRAIT_SIZE,
              }}
            >
              <PortraitImage
                portraitId={portraitId}
                variant={PORTRAIT_VARIANT}
                alt={portraitId.replace(/_/g, ' ')}
                lazy={true}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationWithPortraits;
